import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ApplyTemplateDialog({ eventId, eventType, open, onOpenChange, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: list }, { data: rec }] = await Promise.all([
          api.get("/templates"),
          eventType ? api.get(`/templates/recommend?event_type=${encodeURIComponent(eventType)}`) : Promise.resolve({ data: { key: null } }),
        ]);
        if (cancelled) return;
        setTemplates(list);
        const recKey = rec?.key || null;
        setRecommended(recKey);
        // Auto-select the recommendation if it exists in the list
        if (recKey && list.some(t => t.key === recKey)) {
          setSelected(recKey);
        }
      } catch (e) { console.error("template list/recommend failed:", e); }
    })();
    return () => { cancelled = true; };
  }, [open, eventType]);

  const apply = async () => {
    if (!selected) { toast.error("Pick a template"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/events/${eventId}/apply-template`, { template: selected });
      toast.success(`Added ${data.tasks_added} tasks, ${data.timeline_added} timeline moments, ${data.budget_added} budget lines`);
      onApplied && onApplied();
      onOpenChange(false);
    } catch (e) { toast.error("Could not apply template"); }
    finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface rounded-sm max-w-lg">
        <DialogHeader>
          <div className="eyebrow flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Templates</div>
          <DialogTitle className="font-serif text-2xl text-app">Jump-start with a template</DialogTitle>
          <DialogDescription className="text-sm text-muted font-light">Instantly populate this event with a checklist, day-of timeline, and budget categories tailored to the event type. You can edit or delete anything after.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-80 overflow-y-auto">
          {["detailed", "basic"].map(group => {
            const items = templates.filter(t => (t.type || "detailed") === group);
            if (items.length === 0) return null;
            return (
              <div key={group}>
                <div className="eyebrow mb-2">
                  {group === "detailed" ? "Detailed templates" : "Basic templates (any event type)"}
                </div>
                <div className="space-y-2">
                  {items.map(t => {
                    const isRec = recommended === t.key;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setSelected(t.key)}
                        data-testid={`template-option-${t.key.replace(":", "-")}`}
                        className={`w-full text-left p-3 border rounded-sm transition-colors ${
                          selected === t.key ? "border-[#0A0A0A] bg-surface-2" : "border-hair hover:border-[#0A0A0A]"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-serif text-lg text-app">{t.name}</div>
                          {isRec && (
                            <span
                              data-testid="template-recommended-badge"
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-app text-[#F3EFE7] font-medium"
                            >
                              Recommended
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="apply-template-submit"
            onClick={apply}
            disabled={!selected || applying}
            className="rounded-sm bg-app hover:bg-[#000000]"
          >
            {applying ? "Applying…" : "Apply template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
